import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiCheckboxComponent } from '../../atoms/checkbox/checkbox.component';
import { UiDateInputComponent } from '../../atoms/date-input/date-input.component';
import { UiIconActionComponent } from '../../atoms/icon-action/icon-action.component';
import { UiInputComponent, UiInputKind } from '../../atoms/input/input.component';
import { UiMultiselectComponent } from '../../atoms/multiselect/multiselect.component';
import { UiSelectComponent, UiSelectOption } from '../../atoms/select/select.component';
import { UiTagComponent } from '../../atoms/tag/tag.component';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import {
  formatAmountNumber,
  formatCurrency,
  formatDate,
  formatNumber,
  isNullish,
} from './data-table.formatters';
import {
  getCellLabel,
  getCellTagColor,
  getCellTags,
  getCellText,
  getCellTooltip,
  getCellValue,
  getProjectsList,
  getTagEstatusColor,
} from './data-table.cell-helpers';
import {
  getEffectiveTotal,
  getPagedRows,
  getRangeEnd,
  getRangeStart,
  getTotalPages,
  getVisiblePages,
} from './data-table.pagination';
import { computeDataTableColumnWidths } from './data-table.col-widths';
import {
  UiDataTableActionMenuState,
  UiDataTableActionsMenuComponent,
} from './data-table-actions-menu.component';
import { UiDataTableToolbarComponent } from './data-table-toolbar.component';
import { UiDataTablePaginationComponent } from './data-table-pagination.component';
import {
  DATA_TABLE_MULTI_FILTER_SEP,
  DataGridColumn,
  DataTableCellChange,
  DataTableColumnCheckChange,
  DataTableColumnSizing,
  DataTableEditType,
  DataTableIconButton,
  DataTableSegmentedFilter,
  DataTableTagClick,
  DataTableTagItem,
  DataTableToolbarSelect,
  FilterOptionsModel,
  parseColumnMultiFilter,
  RowAction,
  SortState,
} from './data-table.types';

export type {
  DataGridColumn,
  DataTableCellChange,
  DataTableColumnCheckChange,
  DataTableColumnSizing,
  DataTableEditType,
  DataTableIconButton,
  DataTableSegmentedFilter,
  DataTableTagClick,
  DataTableToolbarSelect,
  FilterOptionsModel,
  RowAction,
  SortState,
};
export type { UiSelectOption };
export { DATA_TABLE_MULTI_FILTER_SEP, parseColumnMultiFilter };

const PAGE_SIZES = [10, 25, 50, 100];

@Component({
  selector: 'ui-data-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiSelectComponent,
    UiMultiselectComponent,
    UiInputComponent,
    UiDateInputComponent,
    UiButtonComponent,
    UiCheckboxComponent,
    UiIconActionComponent,
    UiTagComponent,
    UiTooltipDirective,
    UiDataTableActionsMenuComponent,
    NgTemplateOutlet,
    UiDataTableToolbarComponent,
    UiDataTablePaginationComponent,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  host: {
    '[class.ui-dt-host--fill]': 'verticalScrollFill()',
    '[class.ui-dt-host--borderless]': 'borderless()',
  },
})
export class UiDataTableComponent implements OnDestroy, AfterViewInit {
  private readonly elRef = inject(ElementRef);

  // ── Generales ───────────────────────────────
  readonly title        = input('');
  readonly columns      = input<DataGridColumn[]>([]);
  readonly rows         = input<unknown[]>([]);
  readonly loading      = input(false);
  readonly total        = input(0);
  readonly pageSize     = input(10);

  // ── Toolbar ─────────────────────────────────
  readonly showNewButton    = input(false);
  readonly newLabel         = input('Nuevo');
  /** Muestra el botón de exportar a Excel. Default: true. */
  readonly showExcel        = input(true);
  readonly showStatusFilter = input(false);
  readonly statusFilters    = input<FilterOptionsModel[]>([]);
  readonly segmentedFilters = input<DataTableSegmentedFilter[]>([]);
  readonly toolbarSelects   = input<DataTableToolbarSelect[]>([]);
  /** Muestra el select de registros por página en el toolbar. Default: true. */
  readonly showPageSize     = input(true);
  /** Muestra el pie de paginación y parte las filas. Default: true. */
  readonly showPagination   = input(true);

  // ── Tabla ────────────────────────────────────
  readonly showColumnFilters  = input(true);
  readonly showActionsColumn  = input(false);
  /** Quita el borde y radio exterior cuando la tabla vive dentro de otro contenedor. */
  readonly borderless         = input(false);
  /**
   * Mantiene Acciones fija a la derecha. Puede desactivarse por tabla
   * sin cambiar el comportamiento histórico.
   */
  readonly stickyActionsColumn = input(true);
  readonly actionItems        = input<RowAction[]>([]);
  readonly clickableRows      = input(false);
  readonly showLinkButtons    = input(true);
  /**
   * Limita el alto visible al tamaño seleccionado en el toolbar y habilita
   * scroll vertical. Opt-in; no pagina ni recorta las filas.
   */
  readonly verticalScroll     = input(false);
  /**
   * Con `verticalScroll`, el viewport llena el host flex en lugar de
   * limitarse a `pageSize` filas. Evita que `max-height: none` haga crecer
   * la tabla y provoque scroll en la página.
   */
  readonly verticalScrollFill = input(false);
  /** Habilita el ordenamiento de la tabla. Cada columna puede desactivarlo con `sortable: false`. */
  readonly sortingEnabled     = input(true);
  /**
   * `auto` mide el contenido y solo reduce columnas con `truncate`; `fixed`
   * parte del `width` configurado. Default: `'auto'`.
   */
  readonly columnSizing       = input<DataTableColumnSizing>('auto');

  // ── Totales ──────────────────────────────────
  readonly showTotals      = input(false);
  readonly columnsToTotal  = input<string[]>([]);
  readonly totalLabel      = input('Total');
  readonly totalsOverride  = input<Record<string, number> | null>(null);

  // ── Scroll infinito ──────────────────────────
  readonly infiniteScroll = input(false);

  // ── Edición inline (opt-in) ──────────────────
  /** Activa los editores de celda. Con `true` la tabla deja de paginar. */
  readonly editable = input(false);
  /** Campo identificador de la fila; necesario para editar, alta y selección. */
  readonly rowKey = input<string | null>(null);
  /**
   * Columna de checks a la izquierda, sin título. Opt-in: no cambia las
   * tablas actuales. Requiere `rowKey`.
   */
  readonly selectable = input(false);
  /** Claves de las filas marcadas. Two-way: `[(selectedKeys)]`. */
  readonly selectedKeys = model<(string | number)[]>([]);
  /** Identifica la fila en alta: se edita completa y muestra Guardar / Cancelar. */
  readonly draftRowKey = input<string | number | null>(null);
  /** Columna cuyo editor recibe foco al aparecer una fila en alta. */
  readonly focusDraftColumnKey = input<string | null>(null);
  /**
   * En la fila de alta, cada cambio de celda también emite `cellChange`
   * para que el padre pueda recalcular columnas derivadas antes de Guardar.
   */
  readonly emitDraftChanges = input(false);
  /** Fila bajo la que se proyecta `#rowDetail` (una sola franja `colspan`). */
  readonly detailRowKey = input<string | number | null>(null);
  /** Decide por fila si se proyecta `#rowDetail`. Tiene prioridad sobre `detailRowKey`. */
  readonly detailRowVisible = input<((row: unknown) => boolean) | null>(null);

  // ── Outputs ──────────────────────────────────
  readonly sortChange           = output<SortState>();
  readonly columnFiltersChange  = output<Record<string, string>>();
  readonly statusFilterChange   = output<FilterOptionsModel[]>();
  readonly segmentedFiltersChange = output<DataTableSegmentedFilter[]>();
  readonly toolbarSelectsChange = output<Record<string, UiSelectOption[]>>();
  readonly rowClick             = output<unknown>();
  readonly newClick             = output<void>();
  readonly actionClick          = output<{ row: unknown; action: RowAction }>();
  readonly pageChange           = output<number>();
  readonly scrolledToBottom     = output<void>();
  readonly exportExcel          = output<void>();
  readonly cellChange           = output<DataTableCellChange>();
  readonly columnCheckChange   = output<DataTableColumnCheckChange>();
  readonly tagClick             = output<DataTableTagClick>();
  readonly draftSave            = output<unknown>();
  readonly draftCancel          = output<void>();
  readonly columnButtonClick    = output<{ row: unknown; key: string; buttonId?: string }>();

  readonly rowDetail = contentChild<TemplateRef<unknown>>('rowDetail');

  // ── Estado interno ───────────────────────────
  protected readonly currentPage    = signal(0);
  protected readonly currentSize    = signal(10);
  protected readonly sortState      = signal<SortState | null>(null);
  protected readonly columnFilters  = signal<Record<string, string>>({});
  protected readonly actionMenu = signal<UiDataTableActionMenuState | null>(null);
  /** Valores capturados en la fila borrador, ligados a su `draftRowKey`. */
  private readonly draftEdits = signal<{
    key: string | number | null;
    values: Record<string, string | number | null>;
  } | null>(null);

  /** Texto tal cual lo escribe el usuario, hasta que la celda pierde el foco. */
  private readonly editingCell = signal<{ id: unknown; key: string; raw: string } | null>(null);
  protected readonly pageSizes      = PAGE_SIZES;
  protected readonly pageSizeOptions: UiSelectOption[] = PAGE_SIZES.map(s => ({
    id: String(s), label: `${s} / página`, value: s,
  }));
  protected readonly selectedSizeOption = computed(() =>
    this.pageSizeOptions.find(o => Number(o.value) === this.currentSize()) ?? this.pageSizeOptions[0]
  );
  protected readonly skeletonRows   = Array(6).fill(null);

  // ── Column-width calculation ──────────────────
  protected readonly colWidths  = signal<Record<string, string>>({});
  protected readonly tableWidth = signal('100%');
  protected readonly columnsReady = signal(false);
  protected readonly selectionColWidth = signal('48px');
  protected readonly scrollViewportHeight = computed(() => {
    if (!this.verticalScroll() || this.verticalScrollFill()) return null;
    const headerHeight = 37;
    const filterHeight = this.showColumnFilters() ? 48 : 0;
    const rowHeight = 40;
    const horizontalScrollbarAllowance = 12;
    const computedHeight = `${
      headerHeight +
      filterHeight +
      this.currentSize() * rowHeight +
      horizontalScrollbarAllowance
    }px`;
    return `var(--ui-dt-scroll-max-height, ${computedHeight})`;
  });
  protected readonly stickyLeftOffsets = computed(() => {
    const offsets: Record<string, number> = {};
    let left = this.selectable() ? 48 : 0;
    const widths = this.colWidths();
    for (const column of this.columns()) {
      if (column.sticky !== 'left') continue;
      offsets[column.key] = left;
      left += Number.parseFloat(widths[column.key] ?? '') || column.width || 150;
    }
    return offsets;
  });
  // El canvas de medición vive en el util de col-widths.

  private resizeObserver: ResizeObserver | null = null;
  private sentinelObserver: IntersectionObserver | null = null;
  private readonly scrollHandler = () => { this.closeActionMenu(); };
  private lastFocusedDraftKey: string | number | null = null;

  constructor() {
    document.addEventListener('scroll', this.scrollHandler, { capture: true, passive: true });

    // Recalcular anchos cuando cambian columnas, filas o acciones visibles.
    effect(() => {
      this.columns();
      this.rows();
      this.showActionsColumn();
      this.selectable();
      this.showLinkButtons();
      this.sortingEnabled();
      this.columnSizing();
      this.showTotals();
      this.columnsToTotal();
      this.editable();
      this.verticalScroll();
      setTimeout(() => {
        this.calculateColWidths();
        this.syncStickyHeadOffset();
      }, 0);
    });

    effect(() => {
      const draftKey = this.draftRowKey();
      const columnKey = this.focusDraftColumnKey();
      if (draftKey == null || !columnKey) {
        this.lastFocusedDraftKey = null;
        return;
      }
      if (draftKey === this.lastFocusedDraftKey) return;
      setTimeout(() => this.focusDraftEditor(draftKey, columnKey), 0);
    });
  }

  ngAfterViewInit(): void {
    const host = this.elRef.nativeElement as HTMLElement;
    const scroll = host.querySelector<HTMLElement>('.ui-dt__scroll');
    if (scroll && typeof ResizeObserver !== 'undefined') {
      let previousWidth = scroll.clientWidth;
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if ((entry.target as HTMLElement).classList.contains('ui-dt__head-row')) {
            this.syncStickyHeadOffset();
            continue;
          }
          const width = Math.floor(entry.contentRect.width);
          if (width <= 0 || width === previousWidth) continue;
          previousWidth = width;
          this.closeActionMenu();
          this.calculateColWidths();
          this.syncStickyHeadOffset();
        }
      });
      this.resizeObserver.observe(scroll);
      const header = scroll.querySelector<HTMLElement>('.ui-dt__head-row');
      if (header) this.resizeObserver.observe(header);
    }
    setTimeout(() => {
      this.calculateColWidths();
      this.syncStickyHeadOffset();
    }, 0);
  }

  /** La fila de filtros se fija justo debajo del encabezado medido. */
  private syncStickyHeadOffset(): void {
    const host = this.elRef.nativeElement as HTMLElement;
    const scroll = host.querySelector<HTMLElement>('.ui-dt__scroll');
    if (!scroll) return;
    if (!this.verticalScroll()) {
      scroll.style.removeProperty('--ui-dt-head-h');
      return;
    }
    const header = scroll.querySelector<HTMLElement>('.ui-dt__head-row');
    if (!header) return;
    const height = header.getBoundingClientRect().height;
    if (height <= 0) return;
    scroll.style.setProperty('--ui-dt-head-h', `${height}px`);
  }

  // ── Computed ─────────────────────────────────
  protected readonly showToolbarBar = computed(
    () =>
      this.showExcel() ||
      this.showStatusFilter() ||
      this.segmentedFilters().length > 0 ||
      this.showPageSize() ||
      this.showNewButton() ||
      this.toolbarSelects().length > 0,
  );

  protected readonly effectiveTotal = computed(() =>
    getEffectiveTotal(this.total(), this.rows().length)
  );

  protected readonly totalPages = computed(() =>
    getTotalPages(this.effectiveTotal(), this.currentSize())
  );

  protected readonly pagedRows = computed(() => {
    // En modo editable la lista es corta y la fila en alta se agrega al final:
    // paginar escondería el borrador recién insertado.
    if (this.editable() || !this.showPagination()) return this.rows();
    return getPagedRows(this.rows(), this.currentPage(), this.currentSize());
  });

  protected readonly rangeStart = computed(() =>
    getRangeStart(this.effectiveTotal(), this.currentPage(), this.currentSize())
  );

  protected readonly rangeEnd = computed(() =>
    getRangeEnd(this.effectiveTotal(), this.currentPage(), this.currentSize())
  );

  protected readonly visiblePages = computed(() => {
    return getVisiblePages(this.totalPages(), this.currentPage());
  });

  protected readonly colspan = computed(
    () =>
      this.columns().length +
      (this.showActionsColumn() ? 1 : 0) +
      (this.selectable() ? 1 : 0),
  );

  private selectionRowId(row: unknown): string | number | null {
    const key = this.rowKey();
    if (!key || row == null || typeof row !== 'object') return null;
    const value = (row as Record<string, unknown>)[key];
    return typeof value === 'string' || typeof value === 'number' ? value : null;
  }

  protected readonly visibleSelectableKeys = computed(() => {
    if (!this.selectable()) return [];
    const keys: (string | number)[] = [];
    for (const row of this.pagedRows()) {
      if (this.isDraftRow(row)) continue;
      const id = this.selectionRowId(row);
      if (id != null) keys.push(id);
    }
    return keys;
  });

  protected readonly selectedKeySet = computed(() => new Set(this.selectedKeys()));

  protected readonly allVisibleSelected = computed(() => {
    const keys = this.visibleSelectableKeys();
    if (keys.length === 0) return false;
    const selected = this.selectedKeySet();
    return keys.every(key => selected.has(key));
  });

  protected readonly someVisibleSelected = computed(() => {
    const keys = this.visibleSelectableKeys();
    if (keys.length === 0) return false;
    const selected = this.selectedKeySet();
    return keys.some(key => selected.has(key));
  });

  protected readonly selectAllIndeterminate = computed(
    () => this.someVisibleSelected() && !this.allVisibleSelected(),
  );

  protected isRowSelected(row: unknown): boolean {
    const id = this.selectionRowId(row);
    return id != null && this.selectedKeySet().has(id);
  }

  protected onToggleRow(row: unknown, checked: boolean): void {
    const id = this.selectionRowId(row);
    if (id == null) return;
    const next = new Set(this.selectedKeys());
    if (checked) next.add(id);
    else next.delete(id);
    this.selectedKeys.set([...next]);
  }

  protected onToggleAll(checked: boolean): void {
    const visible = this.visibleSelectableKeys();
    const next = new Set(this.selectedKeys());
    if (checked) {
      for (const key of visible) next.add(key);
    } else {
      for (const key of visible) next.delete(key);
    }
    this.selectedKeys.set([...next]);
  }

  protected readonly computedTotals = computed(() => {
    const override = this.totalsOverride();
    if (override) return override;
    const result: Record<string, number> = {};
    for (const key of this.columnsToTotal()) {
      result[key] = (this.rows() as Record<string, unknown>[]).reduce(
        (sum, row) => sum + (Number(row[key]) || 0), 0
      );
    }
    return result;
  });

  protected stickyLeft(column: DataGridColumn): number | null {
    return column.sticky === 'left'
      ? (this.stickyLeftOffsets()[column.key] ?? 0)
      : null;
  }

  protected isLastStickyLeft(column: DataGridColumn): boolean {
    if (column.sticky !== 'left') return false;
    const stickyColumns = this.columns().filter(item => item.sticky === 'left');
    return stickyColumns[stickyColumns.length - 1]?.key === column.key;
  }

  // ── Sort ─────────────────────────────────────
  protected isColumnSortable(col: DataGridColumn): boolean {
    return this.sortingEnabled() && col.sortable !== false;
  }

  protected isBooleanCheckboxColumn(col: DataGridColumn): boolean {
    return col.type === 'boolean' && col.editable === true;
  }

  protected isCellVisible(row: unknown, col: DataGridColumn): boolean {
    return col.cellVisible?.(row) !== false;
  }

  private checkableRows(col: DataGridColumn): unknown[] {
    return this.pagedRows().filter(row => this.isCellVisible(row, col));
  }

  protected isColumnAllChecked(col: DataGridColumn): boolean {
    const rows = this.checkableRows(col);
    return rows.length > 0 && rows.every(row => !!getCellValue(row, col));
  }

  protected isColumnCheckIndeterminate(col: DataGridColumn): boolean {
    const rows = this.checkableRows(col);
    const some = rows.some(row => !!getCellValue(row, col));
    return some && !this.isColumnAllChecked(col);
  }

  protected isColumnCheckDisabled(col: DataGridColumn): boolean {
    return this.checkableRows(col).length === 0;
  }

  protected onToggleBooleanCell(
    row: unknown,
    col: DataGridColumn,
    checked: boolean,
  ): void {
    if (!this.isCellVisible(row, col)) return;
    this.cellChange.emit({ row, key: col.key, value: checked });
  }

  protected onToggleColumnCheck(col: DataGridColumn, checked: boolean): void {
    this.columnCheckChange.emit({ key: col.key, checked });
  }

  protected onHeaderClick(col: DataGridColumn): void {
    if (!this.isColumnSortable(col)) return;
    const prev = this.sortState();
    const next: SortState =
      prev?.sortBy === col.key
        ? { sortBy: col.key, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { sortBy: col.key, order: 'asc' };
    this.sortState.set(next);
    this.sortChange.emit(next);
  }

  protected sortIcon(col: DataGridColumn): string {
    const s = this.sortState();
    if (!s || s.sortBy !== col.key) return 'both';
    return s.order;
  }

  // ── Column filters ───────────────────────────
  protected onColumnFilter(key: string, value: string): void {
    const updated = { ...this.columnFilters(), [key]: value };
    this.columnFilters.set(updated);
    this.currentPage.set(0);
    this.columnFiltersChange.emit(updated);
  }

  onColumnFilterSelect(key: string, option: UiSelectOption | null): void {
    this.onColumnFilter(key, option ? String(option.value ?? option.label) : '');
  }

  onColumnFilterMulti(key: string, options: UiSelectOption[]): void {
    const value = options
      .map(o => String(o.value ?? o.label))
      .join(DATA_TABLE_MULTI_FILTER_SEP);
    this.onColumnFilter(key, value);
  }

  protected filterValue(key: string): string {
    return this.columnFilters()[key] ?? '';
  }

  filterOptionsOf(col: DataGridColumn): UiSelectOption[] {
    return [...(col.filterOptions ?? [])] as UiSelectOption[];
  }

  filterSelectedOption(col: DataGridColumn): UiSelectOption | null {
    const value = this.filterValue(col.key);
    if (!value) return null;
    return this.filterOptionsOf(col).find(o => String(o.value ?? o.label) === value) ?? null;
  }

  /** Opciones seleccionadas en el filtro multiselect de una columna. */
  filterSelectedMultiOptions(col: DataGridColumn): UiSelectOption[] {
    const selectedValues = parseColumnMultiFilter(this.filterValue(col.key));
    if (!selectedValues.length) {
      return [];
    }
    const selected = new Set(selectedValues);
    return this.filterOptionsOf(col).filter(option =>
      selected.has(String(option.value ?? option.label)),
    );
  }

  // ── Pagination ───────────────────────────────
  protected goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.pageChange.emit(page);
  }

  protected prevPage(): void { this.goToPage(this.currentPage() - 1); }
  protected nextPage(): void { this.goToPage(this.currentPage() + 1); }

  protected onSizeChange(size: number): void {
    this.currentSize.set(size);
    this.currentPage.set(0);
    this.pageChange.emit(0);
  }

  // ── Rows ─────────────────────────────────────
  protected onRowClick(row: unknown, event: MouseEvent): void {
    if (!this.clickableRows()) return;
    // Capturar en una celda no es seleccionar la fila. Se descarta por el
    // origen del clic y no con stopPropagation, que impediría a otros
    // componentes (selects, menús) cerrarse al escuchar el clic del documento.
    const target = event.target as HTMLElement | null;
    if (target?.closest('.ui-dt__editor')) return;
    this.rowClick.emit(row);
  }

  // ── Cell helpers (delegados a data-table.cell-helpers.ts) ───────────────
  protected cellValue    = getCellValue;
  protected cellLabel    = getCellLabel;
  protected cellTagColor = getCellTagColor;
  protected tooltipText  = getCellTooltip;
  protected projectsList = getProjectsList;
  protected cellTags = getCellTags;
  protected tagEstatusColor = getTagEstatusColor;
  protected isNullish    = isNullish;
  protected formatNumber   = formatNumber;
  protected formatAmountNumber = formatAmountNumber;
  protected formatCurrency = formatCurrency;
  protected formatDate     = formatDate;

  // ── Edición inline ───────────────────────────
  protected trackRow(index: number, row: unknown): unknown {
    const key = this.rowKey();
    if (!key) return index;
    return (row as Record<string, unknown>)[key] ?? index;
  }

  protected readonly hasDraftRow = computed(
    () => this.editable() && this.rowKey() != null && this.draftRowKey() != null,
  );

  protected isDraftRow(row: unknown): boolean {
    if (!this.hasDraftRow()) return false;
    const key = this.rowKey() as string;
    return (row as Record<string, unknown>)[key] === this.draftRowKey();
  }

  protected isDetailRow(row: unknown): boolean {
    const visible = this.detailRowVisible();
    if (visible) return visible(row);
    const key = this.rowKey();
    const detail = this.detailRowKey();
    if (!key || detail == null) return false;
    return (row as Record<string, unknown>)[key] === detail;
  }

  protected columnButtonVisible(row: unknown, col: DataGridColumn): boolean {
    return col.buttonVisible?.(row) !== false;
  }

  protected columnButtonLabel(row: unknown, col: DataGridColumn): string {
    return col.buttonLabelForRow?.(row) ?? col.buttonLabel ?? col.header;
  }

  protected onColumnButton(
    row: unknown,
    col: DataGridColumn,
    event: Event,
    buttonId?: string,
  ): void {
    event.stopPropagation();
    this.columnButtonClick.emit({ row, key: col.key, buttonId });
  }

  protected onIconButton(
    row: unknown,
    col: DataGridColumn,
    btn: DataTableIconButton,
    event?: Event,
  ): void {
    event?.stopPropagation();
    this.columnButtonClick.emit({ row, key: col.key, buttonId: btn.id });
  }

  protected isClickableStringCell(col: DataGridColumn): boolean {
    return col.type === 'string' && col.clickable === true;
  }

  protected onStringCellClick(row: unknown, col: DataGridColumn, event: Event): void {
    if (!this.isClickableStringCell(col)) return;
    event.stopPropagation();
    const label = String(this.cellLabel(row, col) ?? '');
    this.tagClick.emit({
      row,
      key: col.key,
      tag: { label, estatus: 'Activo' },
      origin: event.currentTarget instanceof HTMLElement ? event.currentTarget : undefined,
    });
  }

  protected onTagClick(
    row: unknown,
    col: DataGridColumn,
    tag: DataTableTagItem,
    event: Event,
  ): void {
    if (!col.clickable) return;
    event.stopPropagation();
    this.tagClick.emit({
      row,
      key: col.key,
      tag,
      origin: event.currentTarget instanceof HTMLElement ? event.currentTarget : undefined,
    });
  }

  protected isCellEditable(col: DataGridColumn): boolean {
    return this.editable() && col.editable === true;
  }

  protected editorType(col: DataGridColumn, row?: unknown): DataTableEditType {
    if (col.editTypeForRow && row != null) {
      const source = this.isDraftRow(row)
        ? { ...(row as Record<string, unknown>), ...this.draftValues() }
        : row;
      return col.editTypeForRow(source);
    }
    if (col.editType) return col.editType;
    if (col.type === 'currency') return 'amount';
    if (col.type === 'number') return 'number';
    if (col.type === 'date') return 'date';
    return 'text';
  }

  /** Variante del átomo `ui-input` según el editor de la columna. */
  protected editorInputKind(col: DataGridColumn, row?: unknown): UiInputKind {
    const type = this.editorType(col, row);
    if (type === 'amount') return 'amount';
    if (type === 'number') return 'number';
    return 'text';
  }

  private isNumericEditor(col: DataGridColumn, row?: unknown): boolean {
    const type = this.editorType(col, row);
    return type === 'number' || type === 'amount';
  }

  protected editorOptions(col: DataGridColumn, row?: unknown): UiSelectOption[] {
    if (col.editOptionsForRow && row != null) {
      const source = this.isDraftRow(row)
        ? { ...(row as Record<string, unknown>), ...this.draftValues() }
        : row;
      return col.editOptionsForRow(source) as UiSelectOption[];
    }
    return (col.editOptions ?? col.filterOptions ?? []) as UiSelectOption[];
  }

  protected editorValue(row: unknown, col: DataGridColumn): string {
    const editing = this.editingCell();
    if (editing && editing.key === col.key && editing.id === this.rowId(row)) return editing.raw;
    const value = this.cellEditValue(row, col);
    return value == null ? '' : String(value);
  }

  protected editorSelected(row: unknown, col: DataGridColumn): UiSelectOption | null {
    const value = this.cellEditValue(row, col);
    if (value == null || value === '') return null;
    return this.editorOptions(col, row).find(o => String(o.value ?? o.label) === String(value)) ?? null;
  }

  protected onCellEdit(row: unknown, col: DataGridColumn, raw: string): void {
    this.editingCell.set({ id: this.rowId(row), key: col.key, raw });
    const value = this.isNumericEditor(col, row) ? this.parseNumber(raw) : raw;
    this.setCellValue(row, col, value);
  }

  protected onEditorBlur(): void { this.editingCell.set(null); }

  protected onCellSelect(row: unknown, col: DataGridColumn, option: UiSelectOption | null): void {
    this.setCellValue(row, col, option ? String(option.value ?? option.label) : null);
    if (!option || !col.editAdvancesTo) return;
    const nextKey = col.editAdvancesTo;
    const openTrigger = (this.elRef.nativeElement as HTMLElement)
      .querySelector<HTMLElement>('ui-select [aria-expanded="true"]');
    const tableRow = openTrigger?.closest('tr');
    if (!tableRow) return;
    setTimeout(() => {
      setTimeout(() => this.focusCellEditor(tableRow, nextKey), 0);
    }, 0);
  }

  protected readonly draftSaveDisabled = computed(() => {
    const row = this.draftRow();
    if (!row) return true;
    const values = this.draftValues();
    return this.columns().some(col => {
      if (!col.editable || !col.required) return false;
      const value = col.key in values ? values[col.key] : (row[col.key] as unknown);
      return value == null || String(value).trim() === '';
    });
  });

  protected onDraftSave(): void {
    const row = this.draftRow();
    if (!row || this.draftSaveDisabled()) return;
    this.draftSave.emit({ ...row, ...this.draftValues() });
    this.draftEdits.set(null);
    this.editingCell.set(null);
  }

  protected onDraftCancel(): void {
    this.draftEdits.set(null);
    this.editingCell.set(null);
    this.draftCancel.emit();
  }

  private focusDraftEditor(
    draftKey: string | number,
    columnKey: string,
  ): void {
    const host = this.elRef.nativeElement as HTMLElement;
    const draftRow = host.querySelector<HTMLElement>('.ui-dt__row--draft');
    if (!draftRow || !this.focusCellEditor(draftRow, columnKey, true)) return;
    this.lastFocusedDraftKey = draftKey;
  }

  private focusCellEditor(
    tableRow: HTMLElement,
    columnKey: string,
    openSelect = false,
  ): boolean {
    const cells = Array.from(tableRow.querySelectorAll<HTMLElement>('[data-column-key]'));
    const cell = cells.find(element => element.dataset['columnKey'] === columnKey);
    const editor = cell?.querySelector<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!editor) return false;
    editor.focus();
    if (openSelect && editor instanceof HTMLButtonElement && editor.closest('ui-select')) {
      editor.click();
    }
    return true;
  }

  private readonly draftRow = computed<Record<string, unknown> | null>(() => {
    if (!this.hasDraftRow()) return null;
    const key = this.rowKey() as string;
    const rows = this.rows() as Record<string, unknown>[];
    return rows.find(r => r[key] === this.draftRowKey()) ?? null;
  });

  private rowId(row: unknown): unknown {
    const key = this.rowKey();
    return key ? (row as Record<string, unknown>)[key] : row;
  }

  private draftValues(): Record<string, string | number | null> {
    const edits = this.draftEdits();
    return edits && edits.key === this.draftRowKey() ? edits.values : {};
  }

  private cellEditValue(row: unknown, col: DataGridColumn): string | number | null {
    if (this.isDraftRow(row)) {
      const values = this.draftValues();
      if (col.key in values) return values[col.key];
    }
    const raw = getCellValue(row, col);
    return raw == null ? null : (raw as string | number);
  }

  private setCellValue(row: unknown, col: DataGridColumn, value: string | number | null): void {
    const resets = Object.fromEntries((col.editResets ?? []).map(key => [key, null]));
    if (this.isDraftRow(row)) {
      this.draftEdits.set({
        key: this.draftRowKey(),
        values: { ...this.draftValues(), ...resets, [col.key]: value },
      });
      if (this.emitDraftChanges()) {
        this.cellChange.emit({ row, key: col.key, value });
      }
      return;
    }
    this.cellChange.emit({ row, key: col.key, value });
    for (const key of col.editResets ?? []) {
      this.cellChange.emit({ row, key, value: null });
    }
  }

  private parseNumber(raw: string): number | null {
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  // ── Actions menu ─────────────────────────────
  protected openActionMenu(event: MouseEvent, row: unknown, idx: number): void {
    event.stopPropagation();
    if (this.actionMenu()?.rowIndex === idx) {
      this.closeActionMenu();
      return;
    }
    const td = (event.currentTarget as HTMLElement).closest('td') as HTMLElement;
    const rect = (td ?? event.currentTarget as HTMLElement).getBoundingClientRect();

    const items = this.rowActions(row);
    if (!items.length) return;

    const ITEM_H = 36;
    const PAD    = 8;
    const estimatedMenuH = items.length * ITEM_H + PAD;
    const openAbove = rect.top + estimatedMenuH > window.innerHeight;

    this.actionMenu.set({
      row,
      rowIndex: idx,
      top: openAbove ? rect.bottom : rect.top,
      left: rect.left,
      openAbove,
      items,
    });
  }

  protected rowActions(row: unknown): RowAction[] {
    return this.actionItems().filter(action => action.visible?.(row) !== false);
  }

  protected closeActionMenu(): void { this.actionMenu.set(null); }

  protected onActionItem(action: RowAction): void {
    const menu = this.actionMenu();
    if (!menu) return;
    this.actionClick.emit({ row: menu.row, action });
    this.closeActionMenu();
  }

  // ── Filter menu ──────────────────────────────
  protected onFilterMenuChange(groups: FilterOptionsModel[]): void {
    this.statusFilterChange.emit(groups);
  }

  protected readonly activeFilterChips = computed(() =>
    this.statusFilters().flatMap(group =>
      group.values
        .filter(v => v.selected)
        .map(v => ({
          key:         `${group.prefix}::${v.value}`,
          label:       `${group.name} (${v.name})`,
          groupPrefix: group.prefix,
          value:       v.value,
        })),
    ),
  );

  protected removeFilterChip(groupPrefix: string, value: string): void {
    const updated = this.statusFilters().map(g =>
      g.prefix === groupPrefix
        ? { ...g, values: g.values.map(v => v.value === value ? { ...v, selected: false } : v) }
        : g,
    );
    this.statusFilterChange.emit(updated);
  }

  // ── Excel ────────────────────────────────────
  protected onExportExcel(): void { this.exportExcel.emit(); }

  // ── Nuevo ────────────────────────────────────
  protected onNew(): void { this.newClick.emit(); }

  // ── Global click para cerrar paneles ─────────
  @HostListener('document:click')
  onDocClick(): void { this.closeActionMenu(); }

  @HostListener('keydown.escape')
  onEsc(): void { this.closeActionMenu(); }

  @HostListener('window:resize')
  onResize(): void {
    this.closeActionMenu();
    this.calculateColWidths();
  }

  private getScrollContainerWidth(): number {
    const host = this.elRef.nativeElement as HTMLElement;
    const scroll = host.querySelector('.ui-dt__scroll');
    return Math.floor(
      scroll ? (scroll as HTMLElement).clientWidth : host.offsetWidth,
    );
  }

  // ── Column-width algorithm ────────────────────
  private calculateColWidths(): void {
    if (this.getScrollContainerWidth() <= 0) return;
    const rows = this.rows() as Record<string, unknown>[];
    const measureRows =
      this.showTotals() && rows.length > 0
        ? [...rows, this.computedTotals() as Record<string, unknown>]
        : rows;

    const result = computeDataTableColumnWidths({
      columns: this.columns(),
      rows: measureRows,
      containerWidth: this.getScrollContainerWidth(),
      showActionsColumn: this.showActionsColumn(),
      showSelectionColumn: this.selectable(),
      showLinkButtons: this.showLinkButtons(),
      sortingEnabled: this.sortingEnabled(),
      columnSizing: this.columnSizing(),
      getCellText,
    });

    this.tableWidth.set(result.tableWidth);
    this.colWidths.set(result.colWidths);
    this.selectionColWidth.set(result.selectionWidth);
    this.columnsReady.set(true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.scrollHandler, { capture: true });
    this.resizeObserver?.disconnect();
    this.sentinelObserver?.disconnect();
  }
}
